import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { STAFF_PROXY_TESTING_DEVICE_DETAIL } from '@staff-app/graphql-client';
import { safeJsonParse } from '@/lib/safe-format';
import type { TestingDeviceDetail } from '../components/testing-drawer/lib/types';
import { customerReadVars } from '../lib/drawer-mode';

interface Result {
  staffProxyTestingDeviceDetail: string | null;
}

/**
 * open-api `getDeviceDetail` for ONE device (via staff-api). Without `accountId` it is the testing
 * read — pass `skip` for any row that is not a testing device, the op 403s for those. With
 * `accountId` (the customer drawer) staff-api's customer gate applies instead: the device must be on
 * that account.
 */
export function useTestingDeviceDetail(
  deviceId: string | null | undefined,
  skip: boolean,
  accountId?: string,
) {
  const { data, loading, error, refetch } = useQuery<Result>(STAFF_PROXY_TESTING_DEVICE_DETAIL, {
    variables: { deviceId: deviceId ?? '', ...customerReadVars(accountId) },
    skip: skip || !deviceId,
    fetchPolicy: 'cache-and-network',
  });

  const detail = useMemo(
    () => safeJsonParse<TestingDeviceDetail | null>(data?.staffProxyTestingDeviceDetail, null),
    [data],
  );

  return { detail, loading, error, refetch };
}
